import React, { lazy, Suspense, useCallback } from 'react';
import { Controller, useFormState } from 'react-hook-form';
import { addToast } from '@heroui/react';

import type { FieldValues, FieldPath, RegisterOptions, Control } from 'react-hook-form';
import type { IAllProps } from '@tinymce/tinymce-react';
import { IMAGE_MIME_TYPES, uploadFileToS3 } from '@/utils/uploadFileToS3';

const Editor = lazy<React.ComponentType<IAllProps>>(() =>
  import('@tinymce/tinymce-react').then((module) => ({
    default: module.Editor as React.ComponentType<IAllProps>,
  })),
);

type LazyRichTextEditorProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  control: Control<TFieldValues>;
  name: TName;
  defaultValue?: string;
  rules?: RegisterOptions<TFieldValues, TName>;
};

const RICH_TEXT_IMAGE_MAX_SIZE_MB = 10;

export const LazyRichTextEditor = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  defaultValue,
  rules = {},
}: LazyRichTextEditorProps<TFieldValues, TName>) => {
  const { isSubmitted } = useFormState({ control });

  const handleImageUpload = useCallback((blobInfo: { blob: () => Blob; filename: () => string }, progress: (percent: number) => void) => {
    const blob = blobInfo.blob();
    const filename = blobInfo.filename() || `image-${Date.now()}.png`;
    const file = new File([blob], filename, { type: blob.type || 'image/png' });

    return uploadFileToS3({
      file,
      filename,
      maxSizeMb: RICH_TEXT_IMAGE_MAX_SIZE_MB,
      isMediaLibrary: false,
      allowedMimeTypes: IMAGE_MIME_TYPES,
      onProgress: progress,
    })
      .then((metadata) => metadata.url)
      .catch((err) => {
        const errorMessage = err instanceof Error ? err.message : 'Failed to upload image';
        addToast({
          title: 'Upload Error',
          description: errorMessage,
          color: 'danger',
        });
        return Promise.reject(err);
      });
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue as any}
      rules={rules}
      render={({ field: { onChange, value }, fieldState: { error, isTouched } }) => {
        const showError = (isTouched || isSubmitted) && error;
        return (
          <div className="w-full">
            <Suspense
              fallback={
                <div className="border border-gray-300 rounded p-4 bg-gray-50 animate-pulse">
                  <div className="h-8 bg-gray-200 rounded mb-2"></div>
                  <div className="h-64 bg-gray-200 rounded"></div>
                </div>
              }
            >
              <Editor
                licenseKey="gpl"
                tinymceScriptSrc={'/tinymce/tinymce.min.js'}
                value={value}
                onEditorChange={onChange}
                init={{
                  height: 400,
                  menubar: false,
                  automatic_uploads: true,
                  paste_data_images: true,
                  images_file_types: 'jpeg,jpg,png,gif,webp',
                  images_reuse_filename: true,
                  images_upload_handler: handleImageUpload,
                  plugins: [
                    'advlist',
                    'autolink',
                    'lists',
                    'link',
                    'image',
                    'charmap',
                    'anchor',
                    'searchreplace',
                    'visualblocks',
                    'code',
                    'fullscreen',
                    'insertdatetime',
                    'media',
                    'table',
                    'preview',
                    'help',
                    'wordcount',
                  ],
                  toolbar:
                    'undo redo | blocks | ' +
                    'bold italic forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | image | help',
                  content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                }}
              />
            </Suspense>
            {showError && <p className="text-red-500 text-sm mt-1">{error.message}</p>}
          </div>
        );
      }}
    />
  );
};
